import {
  ToQueryStringFilters,
  useOrderByFilter,
  usePagination,
  useToQueryString,
} from '@/core'
import { Pagination, TImportFile, TRecordAction } from '@/core/types'
import { TUser } from '@/modules/auth'
import { createInjectionState, toReactive } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useAuth } from 'vue-auth3'
import type { TLead, TLeadMassiveEdit } from '../../../types'
import { useCatalogQuery } from '../query/useCatalogQuery'
import { useModuleQuery } from '../query/useModuleQuery'
import { useDate } from '@/core'
import { useCatalogStore } from '@/core/store/catalogs'
import { storeToRefs } from 'pinia'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<TLead[]>([])
  const form = ref<TLead>({})
  const formLeadMassiveEdit = ref<TLeadMassiveEdit>({})
  const store = useCatalogStore()
  const { catalogs } = storeToRefs(store)

  const filters = ref<ToQueryStringFilters>({
    param: '',
    by_advisor: [],
    project_ids: [],
    lot_ids: [],
    by_status: [],
    dates: {
      created_at: [],
    },
    order_by: 'created_at,desc',
  })

  const advisorFilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const projectFilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const lotFilters = ref<ToQueryStringFilters>({
    param: '',
    status_id: [25],
  })
  const { zonedTimeToUtc } = useDate()

  const advisorsQueryParams = useToQueryString(toReactive(advisorFilters), {
    searchColumns: ['name'],
  })

  const projectsQueryParams = useToQueryString(toReactive(projectFilters), {
    searchColumns: ['name'],
  })

  const lotsQueryParams = useToQueryString(toReactive(lotFilters), {
    searchColumns: ['name'],
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['first_name', 'last_name', 'phone_number'],
    dateSettings: {
      enableFormat: true,
    },
  })

  const modulePagination = usePagination()

  const auth = useAuth()

  const moduleQuery = useModuleQuery({
    currentPage: modulePagination.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
      loadRecord: false,
    },
    handlers: {
      onLoadRecords: (pagination: Pagination) => {
        modulePagination.setPagination(pagination)
      },
    },
  })

  const catalogQuery = useCatalogQuery({
    filters: {
      advisorsCatalog: advisorsQueryParams.queryParams,
      projectsCatalog: projectsQueryParams.queryParams,
      leadsLotsCatalog: lotsQueryParams.queryParams,
    },
    enableQuery: {
      campaignsCatalog: false,
      originsCatalog: false,
      statusesCatalog: true,
      advisorsCatalog: true,
      projectsCatalog: true,
      lotsCatalog: false,
      leadsLotsCatalog: computed(
        () => lotFilters.value?.project_ids?.length > 0,
      ),
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at', 'updated_at'],
  })

  const onAdd = async (payload: TLead) => {
    await moduleQuery.addRecord.mutateAsync({
      ...payload,
      created_at: zonedTimeToUtc(new Date(payload.created_at ?? ''), {
        removeTimeZone: false,
      }),
      ...(!!payload.email || !!payload.email?.length
        ? { email: payload.email.toLowerCase() }
        : {}),
    })
  }

  const onEdit = async (payload: TLead) => {
    // eslint-disable-next-line prefer-const
    let data = { ...payload }

    if (!data.email || !data.email.length) {
      delete data.email
    }

    await moduleQuery.editRecord.mutateAsync({
      ...data,
      created_at: zonedTimeToUtc(new Date(payload.created_at ?? ''), {
        removeTimeZone: false,
      }),
    })
  }

  const onDelete = async (payload: TLead) => {
    await moduleQuery.deleteRecord.mutateAsync(payload)
  }

  const onImport = async (data: TImportFile) => {
    await moduleQuery.importRecords.mutateAsync(data)
  }

  const onExport = async () => {
    await moduleQuery.exportRecords()
  }

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
  }

  const onMassiveEdit = async (payload: TLeadMassiveEdit) => {
    await moduleQuery.massiveEditRecords.mutateAsync(payload)
  }

  const onRequestCatalogs = async (payload: number[]) => {
    await catalogQuery.requestCatalogs.mutateAsync({
      ids: payload,
    })
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const totalLeads = computed(
    () => moduleQuery.loadRecords.data?.value?.meta?.total,
  )

  const advisorsCatalog = computed(
    () => catalogQuery.advisorsCatalog.data?.value,
  )

  const statusesCatalog = computed(
    () => catalogQuery.statusesCatalog.data?.value,
  )

  const projectsCatalog = computed(
    () => catalogQuery.projectsCatalog.data?.value,
  )

  const leadsLotsCatalog = computed(
    () => catalogQuery.leadsLotsCatalog.data?.value,
  )

  const user = computed(() => auth.user<TUser>())

  const isLoading = computed(
    () =>
      moduleQuery.addRecord.isLoading.value ||
      moduleQuery.editRecord.isLoading.value ||
      moduleQuery.loadRecords.isLoading.value,
  )

  return {
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    totalLeads,
    isLoading,
    modulePagination: toReactive(modulePagination),
    advisorsCatalog,
    statusesCatalog,
    user,
    advisorFilters,
    formLeadMassiveEdit,
    projectsCatalog,
    projectFilters,
    catalogs,
    leadsLotsCatalog,
    lotFilters,
    onExport,
    onRequestCatalogs,
    onImport,
    onAction,
    onDelete,
    onEdit,
    onAdd,
    onMassiveEdit,
  }
})

export { useModule, useProvideModule }
